import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, TextField, InputAdornment, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { UserDto } from '@vatos-pas/common';

import {
  FormToolbar,
  BooleanInput,
  Autocomplete,
  SnackbarContainer,
} from 'components/standalone';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { useRefreshTrigger } from 'hooks';
import { UserFormValues, RoleOption, RegionOption } from '../containers';
import { validateUser } from '../views/userValidation';
import { createValidationResolver } from 'utils';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { AppRole } from 'providers/roles';

export interface UserFormProps {
  mode: 'create' | 'edit';
  initialValues?: UserDto | null;
  onSubmit: (values: UserFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  // Lazy loading functions for dropdowns
  loadRoleOptions: (searchTerm: string) => Promise<RoleOption[]>;
  loadRegionOptions: (searchTerm: string) => Promise<RegionOption[]>;
  // Initial IDs (edit mode: from API)
  initialRoleIds?: string[];
  initialRegionIds?: string[];
  // Preselection (create mode: contractor flow)
  preselectedRoleName?: string;
  // Optional callback for parent (supervisor check)
  onRolesChange?: (roleIds: string[]) => void;
  // Role disable (contractor flow)
  rolesDisabled?: boolean;
  // Regional Manager (edit only)
  regionalManagerName?: string;
  showRegionalManager?: boolean;
  // Increment to force form reset to API values (error recovery)
  resetTrigger?: number;
  // Deactivation flow
  findInactiveRegion?: () => Promise<RegionOption | null>;
}

const getDefaultValues = (
  initialValues?: UserDto | null,
  initialRoleIds: string[] = [],
  initialRegionIds: string[] = [],
): UserFormValues => ({
  firstName: initialValues?.firstName || '',
  lastName: initialValues?.lastName || '',
  email: initialValues?.email || '',
  primaryPhone: initialValues?.primaryPhone || '',
  idNumber: initialValues?.idNumber || '',
  password: '',
  active: initialValues?.active ?? true,
  selectedRoles: initialRoleIds,
  selectedRegions: initialRegionIds,
});

export const UserForm: React.FC<UserFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  loading,
  onGoBack,
  loadRoleOptions,
  loadRegionOptions,
  initialRoleIds = [],
  initialRegionIds = [],
  preselectedRoleName,
  onRolesChange,
  rolesDisabled = false,
  regionalManagerName,
  showRegionalManager = false,
  resetTrigger = 0,
  findInactiveRegion,
}) => {
  const classes = useStyles();
  const { notify } = SnackbarContainer.useContainer();
  const isCreateMode = mode === 'create';
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  // Regional Admin ticket: what the acting user may change on this form
  const { hasRole } = PermissionsProvider.useContainer();
  const actorIsRegionalAdmin =
    hasRole(AppRole.RegionalAdmin) && !hasRole(AppRole.SuperAdmin);

  const [roleOptionsCache, setRoleOptionsCache] = useState<RoleOption[]>([]);
  const [regionOptionsCache, setRegionOptionsCache] = useState<RegionOption[]>(
    [],
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // Track if preselection has been applied (contractor flow)
  const preselectionApplied = useRef(false);

  // Re-fetch options when toolbar refresh is clicked
  useRefreshTrigger(() => {
    setRoleOptionsCache([]);
    setRegionOptionsCache([]);
    setRefreshKey(k => k + 1);
  });

  // Use mount trigger for edit mode (need to resolve selections immediately)
  // Use focus trigger for create mode (lazy loading)
  // Exception: load immediately when preselectedRoleName is provided (contractor flow)
  const loadTrigger = isCreateMode && !preselectedRoleName ? 'focus' : 'mount';

  const formValues = useMemo(
    () =>
      initialValues
        ? getDefaultValues(initialValues, initialRoleIds, initialRegionIds)
        : undefined,
    [initialValues, initialRoleIds, initialRegionIds],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UserFormValues>({
    defaultValues: getDefaultValues(
      initialValues,
      initialRoleIds,
      initialRegionIds,
    ),
    values: formValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    resolver: createValidationResolver<UserFormValues>(validateUser({ mode })),
    mode: 'onBlur',
  });

  // Watch roles for parent notification (supervisor check)
  const watchedRoles = watch('selectedRoles');

  // Notify parent when roles change (for supervisor role detection)
  useEffect(() => {
    onRolesChange?.(watchedRoles);
  }, [watchedRoles, onRolesChange]);

  // Force reset when resetTrigger changes (error recovery)
  const prevResetTrigger = useRef(resetTrigger);
  useEffect(() => {
    if (
      resetTrigger > 0 &&
      resetTrigger !== prevResetTrigger.current &&
      formValues
    ) {
      reset(formValues);
      prevResetTrigger.current = resetTrigger;
    }
  }, [resetTrigger, formValues, reset]);

  const onFormSubmit = useMemo(
    () => handleSubmit(async data => await onSubmit(data)),
    [handleSubmit, onSubmit],
  );

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleConfirmDeactivate = useCallback(async () => {
    setConfirmDeactivateOpen(false);
    const inactiveRegion = (await findInactiveRegion?.()) ?? null;
    if (!inactiveRegion) {
      notify('Inactive region not found. Please contact support.', 'error');
      setValue('active', true, { shouldDirty: true });
      return;
    }
    if (!regionOptionsCache.find(r => r.id === inactiveRegion.id)) {
      setRegionOptionsCache(prev => [...prev, inactiveRegion]);
    }
    setValue('selectedRegions', [inactiveRegion.id], { shouldDirty: true });
  }, [findInactiveRegion, notify, setValue, regionOptionsCache]);

  const handleCancelDeactivate = useCallback(() => {
    setConfirmDeactivateOpen(false);
    setValue('active', true, { shouldDirty: true });
  }, [setValue]);

  const handleLoadRoleOptions = useMemo(
    () =>
      async (searchTerm: string): Promise<RoleOption[]> => {
        const options = await loadRoleOptions(searchTerm);
        setRoleOptionsCache(options);

        // Contractor flow: preselect role once
        if (preselectedRoleName && !preselectionApplied.current) {
          const role = options.find(r => r.name === preselectedRoleName);
          if (role) {
            setValue('selectedRoles', [role.id]);
            preselectionApplied.current = true;
          } else {
            console.warn(
              `[UserForm] Preselected role "${preselectedRoleName}" not found in loaded options.`,
              { availableRoles: options.map(r => r.name) },
            );
          }
        }

        return options;
      },
    [loadRoleOptions, preselectedRoleName, setValue],
  );

  const handleLoadRegionOptions = useMemo(
    () =>
      async (searchTerm: string): Promise<RegionOption[]> => {
        const options = await loadRegionOptions(searchTerm);
        setRegionOptionsCache(options);
        return options;
      },
    [loadRegionOptions],
  );

  // Helper to get role objects from IDs
  const getRoleObjects = useCallback(
    (roleIds: string[]): RoleOption[] =>
      roleIds
        .map(id => roleOptionsCache.find(r => r.id === id))
        .filter(Boolean) as RoleOption[],
    [roleOptionsCache],
  );

  // Helper to get region objects from IDs
  const getRegionObjects = useCallback(
    (regionIds: string[]): RegionOption[] =>
      regionIds
        .map(id => regionOptionsCache.find(r => r.id === id))
        .filter(Boolean) as RegionOption[],
    [regionOptionsCache],
  );

  // Roles of the user being edited, resolved through the loaded role options
  const targetRoleNames = useMemo(
    () => getRoleObjects(watchedRoles || []).map(r => r.name),
    [getRoleObjects, watchedRoles],
  );
  const targetIsSuperAdmin = targetRoleNames.includes(AppRole.SuperAdmin);
  const targetIsRegionalAdmin = targetRoleNames.includes(AppRole.RegionalAdmin);
  const isAdminRole = (role: RoleOption) =>
    role.name === AppRole.RegionalAdmin || role.name === AppRole.SuperAdmin;
  // Edit only (a new SuperAdmin gets every Region from the API after the picked ones): SuperAdmin Regions are
  // automatic, a Regional Admin cannot change a Regional Admin's, and the target's roles are unknown until loaded.
  const regionsLocked =
    !isCreateMode &&
    (!roleOptionsCache.length ||
      targetIsSuperAdmin ||
      (actorIsRegionalAdmin && targetIsRegionalAdmin));

  const rolesAutocomplete = (
    <Controller
      name="selectedRoles"
      control={control}
      render={({ field, fieldState: { error } }) => {
        const roleObjects = getRoleObjects(field.value || []);
        return (
          <Box className={classes.halfWidth}>
            <Autocomplete<RoleOption>
              key={`roles-${refreshKey}`}
              disableCloseOnSelect
              multiple
              clientSideFilter
              label="Roles"
              loadOptions={handleLoadRoleOptions}
              loadTrigger={loadTrigger}
              value={roleObjects}
              onChange={newValue => field.onChange(newValue.map(r => r.id))}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.id}
              disabled={rolesDisabled}
              getOptionDisabled={actorIsRegionalAdmin ? isAdminRole : undefined}
              isOptionLocked={actorIsRegionalAdmin ? isAdminRole : undefined}
              error={error?.message}
              fullWidth
            />
          </Box>
        );
      }}
    />
  );

  const regionsAutocomplete = (
    <Controller
      name="selectedRegions"
      control={control}
      render={({ field, fieldState: { error } }) => {
        const regionObjects = getRegionObjects(field.value || []);
        return (
          <Box className={classes.halfWidth}>
            <Autocomplete<RegionOption>
              key={`regions-${refreshKey}`}
              disableCloseOnSelect
              multiple
              clientSideFilter
              label="Regions"
              loadOptions={handleLoadRegionOptions}
              loadTrigger={loadTrigger}
              value={regionObjects}
              onChange={newValue => {
                // Regions the acting user cannot see (no loaded option) must survive every change.
                const hidden = (field.value ?? []).filter(
                  (id: string) => !regionObjects.some(r => r.id === id),
                );
                field.onChange([...hidden, ...newValue.map(r => r.id)]);
              }}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.id}
              disabled={regionsLocked}
              error={error?.message}
              fullWidth
            />
          </Box>
        );
      }}
    />
  );

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="First name"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Last name"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
        </Box>

        <Box className={classes.fields} my={3}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Email"
                error={!!errors.email}
                helperText={errors.email?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="primaryPhone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Primary phone"
                error={!!errors.primaryPhone}
                helperText={errors.primaryPhone?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="idNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="ID Number"
                error={!!errors.idNumber}
                helperText={errors.idNumber?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          {isCreateMode ? (
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  className={classes.input}
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  margin="dense"
                  variant="filled"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          ) : actorIsRegionalAdmin ? null : (
            <Box flex={1} className={classes.input}>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <BooleanInput
                    label="Active"
                    checked={field.value}
                    onChange={value => {
                      field.onChange(value);
                      if (
                        mode === 'edit' &&
                        field.value === true &&
                        value === false
                      ) {
                        setConfirmDeactivateOpen(true);
                      }
                    }}
                  />
                )}
              />
            </Box>
          )}
        </Box>

        {isCreateMode ? (
          <Box className={classes.fields} mb={3}>
            {rolesAutocomplete}
            {regionsAutocomplete}
          </Box>
        ) : (
          <>
            <Box className={classes.fields} mb={1.5}>
              {rolesAutocomplete}
            </Box>
            <Box className={classes.fields} mb={1.5}>
              {regionsAutocomplete}
            </Box>
          </>
        )}

        {isCreateMode && !actorIsRegionalAdmin && (
          <Box className={classes.fields}>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <BooleanInput
                  label="Active"
                  checked={field.value}
                  onChange={field.onChange}
                  className={classes.input}
                />
              )}
            />
          </Box>
        )}

        {!isCreateMode && showRegionalManager && (
          <Box className={classes.fields}>
            <TextField
              fullWidth
              className={classes.input}
              label="Regional Manager"
              value={regionalManagerName || ''}
              disabled
              margin="dense"
            />
          </Box>
        )}
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave={true}
      />
      <ConfirmDialog
        open={confirmDeactivateOpen}
        handleClose={handleCancelDeactivate}
        title="Deactivate User"
        content="Setting this user to Inactive will remove all current Regions, and the user will be assigned to the 'Inactive User'. Please remember to save the form to ensure changes are recorded. Do you want to proceed?"
        onConfirm={handleConfirmDeactivate}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {
    margin: '0px 15px',
  },
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  halfWidth: {
    flex: 0.5,
    margin: '0px 15px',
  },
}));
